import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { AdminUser } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';
import { relativeTime } from '../lib/format.js';
import { ErrorBox, Spinner } from '../components/ui.js';

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const a = words[0]?.[0];
  const b = words.length > 1 ? words[words.length - 1]?.[0] : undefined;
  return ((a ?? '?') + (b ?? '')).toUpperCase();
}

/** Company administration: every Jabberr account, and creating accounts so a new person can sign in. */
export function UsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { users: list } = await api.admin.users();
      setUsers(list);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Users</h1>
          {users ? (
            <span className="count">
              {users.length} {users.length === 1 ? 'account' : 'accounts'}
            </span>
          ) : null}
          <p className="page-subtitle muted small">
            Everyone with a Jabberr account. Create an account here, then add the person to projects from each project page.
          </p>
        </div>
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {notice ? (
        <p className="notice-ok" role="status">
          {notice}
        </p>
      ) : null}

      <AddUser
        onCreated={(u, created) => {
          setNotice(created ? `${u.name} can now sign in with a magic link sent to ${u.email}.` : `${u.name} already has an account.`);
          setError(null);
          void load();
        }}
        onError={(msg) => {
          setError(msg);
          setNotice(null);
        }}
      />

      {users === null ? (
        <Spinner label="Loading" />
      ) : (
        <ul className="conv-list user-list">
          {users.map((u) => (
            <li key={u.id} className="conv-row user-row">
              <span className="avatar avatar-lg" aria-hidden="true">
                {initials(u.name)}
              </span>
              <span className="conv-row-main">
                <span className="conv-row-title">{u.name}</span>
                <span className="conv-row-meta muted small">
                  {u.email} · joined {relativeTime(u.createdAt)} · {u.projects} {u.projects === 1 ? 'project' : 'projects'}
                </span>
              </span>
              <span className="chip">member</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AddUser({ onCreated, onError }: { onCreated: (u: AdminUser, created: boolean) => void; onError: (msg: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const { user: u, created } = await api.admin.createUser({ email: email.trim(), name: name.trim() || email.trim() });
      onCreated(u, created);
      setEmail('');
      setName('');
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card add-user-card">
      <h2>Add a user</h2>
      <p className="muted small">The person signs in with a magic link to this address, or with Microsoft if it is their work account.</p>
      <form className="add-user-form" onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Full name"
          />
        </label>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            placeholder="colleague@tarawagroup.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-label="Email"
          />
        </label>
        <label className="field field-action">
          <span className="sr-only">Create account</span>
          <button type="submit" className="btn btn-primary" disabled={busy || !email}>
            Create account
          </button>
        </label>
      </form>
    </div>
  );
}
